/* eslint-disable react/no-unescaped-entities */
import react from 'react'

interface GoogleConnectedAppProps {
  id?: string
  googleManagerId?: string
  connections?: { name: string, customerId: string }[]
  isConnected?: boolean
  sessionId?: string
  oauthCallbackBaseUrl?: string
}

const GoogleConnectedApp = (props: GoogleConnectedAppProps) => {

  const [googleInfoHidden, setGoogleInfoHidden] = react.useState(true)
  const [id, setId] = react.useState(props.id)
  const [connections, setConnections] = react.useState(props.connections ?? [])
  const [connectionsSaved, setConnectionsSaved] = react.useState(false)
  const [googleManagerId, setGoogleManagerId] = react.useState(props.googleManagerId)
  const [managerIdSaved, setManagerIdSaved] = react.useState(false)
  const [isConnected, setIsConnected] = react.useState(props.isConnected)
  const [error, setError] = react.useState('')

	async function handleSaveConfigData(data: {[key: string]: any}) {
    // Save the data
		const resp = await fetch(`/api/google/data`, {
			method: 'post',
			headers: {
				Session: props.sessionId!,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({id, ...data})
		})

    // Handle errors
    if (!resp.ok) {
      console.error('Failed to save config data', resp)
      setError('Error saving data')
      return
    }

    // Parse response
    const json = await resp.json()
    console.info('saved config data', json)
    setId(json.id)

    // Briefly updated the text on the save button
    if (data.connections) {
      setConnectionsSaved(true)

      setTimeout(() => {
        setConnectionsSaved(false)
      }, 4000)
    } else if (data.googleManagerId) {
      setManagerIdSaved(true)

      setTimeout(() => {
        setManagerIdSaved(false)
      }, 4000)
    }

    setError('')
		return json.success
	}

  async function handleDeleteConnection() {
    const resp = await fetch(`/api/google/data`, {
      method: 'delete',
      headers: {
				Session: props.sessionId!,
      }
    })

    if (resp.ok) {
      setIsConnected(false)
    } else {
      console.error('error deleting connection', await resp.text())
      setError('Error deleting connection')
    }
  }

  function updateConnection(idx: number, field: 'name' | 'customerId', value: string) {
    if (!connections) {
      const connection = {name: '', customerId: ''}
      connection[field] = value

      setConnections([connection])
      return 
    }

    // Recreate the connections array w/ the new value
    const newConnections = JSON.parse(JSON.stringify(connections))
    newConnections[idx]![field] = value

    setConnections(newConnections)
  }

  function handleNewGoogleConnection() {
    const newConnections = JSON.parse(JSON.stringify(connections))
    newConnections!.push({ name: '', customerId: '' })

    setConnections(newConnections)
  }

  react.useEffect(() => {
    console.log('updating state', props)
    setId(props.id)
    setConnections(props.connections ?? [])
    setGoogleManagerId(props.googleManagerId)
    setIsConnected(props.isConnected)
  }, [props.id, props.connections, props.googleManagerId, props.isConnected])

  return (
     <div
      className='border-b border-gray-300 mb-5 pb-5 bg-slate-200 p-8'
     >
      <h2 className="text-2xl text-slate-500">
        <span className="text-teal-700 font-extrabold">Google Ads</span><button className='underline text-blue-500 text-sm ml-2' onClick={() => setGoogleInfoHidden(!googleInfoHidden)}>(help)</button>
      </h2>
      <div>
        <section className='my-6'>
          <div className={`${googleInfoHidden ? 'hidden' : 'block'} bg-slate-100 p-8 my-5`}>
            <p className='text-sm'>
              Your Google Ads Customer ID is a 10-digit number that uniquely identifies your Google Ads account. 
              You can find your Customer ID at the top right corner of your Google Ads account.
            </p>
            <img src="/assets/images/google-ads-customer-id.png" alt="Google Ads Customer ID" />  
          </div>

          <div className=''>
            <div>

              <label htmlFor="google-manager-id" className='block mt-6 font-bold'>Manager ID</label>
              <p className='text-sm text-slate-500 italic mt-0 mb-2'>
                  Google Ads requires an MCC for an external connection.  This will connect all of the 
                  associated accounts under the MCC.  If you don't have an MCC, you can create one in 
                  your Google Ads account.
              </p>
              <div className='flex flex-col md:flex-row align-middle'>
                <input 
                  id="google-customer-id" 
                  className="shadow appearance-none border rounded px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-10"
                  type="text"
                  placeholder="472-763-6499"
                  value={googleManagerId}
                  onChange={(e) => setGoogleManagerId(e.target.value)}
                />

                <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white mt-4 md:mt-0 h-8 md:h-10 font-bold md:ml-5 px-4 rounded"
                  onClick={() => handleSaveConfigData({googleManagerId: googleManagerId!})}
                >
                  { managerIdSaved ? ' ✔️ ' : 'Save' }
                </button>
              </div>

              <div className='hidden'>
                <label className='block mt-6'>2. Customer Account ID(s)</label>
                <p className='text-sm text-slate-500 italic mt-0 mb-2'>Name and save the Customer Account IDs you want synced.  Records from this source will have a "source" parameter equal to the name.</p>

                { 
                  connections?.map((c, idx) => {
                    return (
                    <div className='flex flex-row items-end' key={idx}>
                      <label htmlFor="google-name" className='block text-sm'>Name (must be unique)<br/>
                        <input 
                          id="google-name" 
                          className="shadow appearance-none border rounded px-3 text-gray-700 leading-tight text-xl focus:outline-none focus:shadow-outline h-10"
                          type="text"
                          placeholder="adwords"
                          value={connections[idx]!.name}
                          onChange={(e) => updateConnection(idx, 'name', e.target.value)}
                        />
                      </label>
      
                      <label htmlFor="google-customer-id" className='ml-5 block text-sm'>Customer ID<br/>
                        <input 
                          id="google-customer-id" 
                          className="shadow appearance-none border rounded px-3 text-gray-700 text-xl leading-tight focus:outline-none focus:shadow-outline h-10"
                          type="text"
                          placeholder="810-650-3350"
                          value={connections[idx]!.customerId}
                          onChange={(e) => updateConnection(idx, 'customerId', e.target.value)}
                        />
                      </label>
      
                      <button 
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold ml-5 px-4 h-10 rounded"
                        onClick={() => handleSaveConfigData({ connections })}
                      >
                        { connectionsSaved ? ' ✔️ ' : 'Save' }
                      </button>
                    </div>
                    )
                  })
                }
              </div>
              <button onClick={() => handleNewGoogleConnection()} className='hidden underline text-blue-500 mt-4'>Add another</button>
            </div>
          </div>
        </section>

        <h4 className='mb-2 font-bold'>Connect your Google Ads account</h4>
        { !isConnected &&
          <div className='text-med'>
            <a 
              className='inline-block border border-blue-500 bg-blue-500 text-white font-bold py-2 px-4 rounded mt-4'
              href={`https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/adwords&access_type=offline&include_granted_scopes=true&response_type=code&state=state_parameter_passthrough_value&redirect_uri=${props.oauthCallbackBaseUrl}/oauth-callback/google&client_id=202417950362-addmtvc27k9devup7iirurrvjg6c64bi.apps.googleusercontent.com&prompt=consent`}>
            Authorize Google</a>
          </div>
        }
        { isConnected && // prompt=consent parameter forces a new refresh token
          <div className='text-med'>
            Done ✅ <br />
            <a 
              href={`https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/adwords&access_type=offline&include_granted_scopes=true&response_type=code&state=state_parameter_passthrough_value&redirect_uri=${props.oauthCallbackBaseUrl}/oauth-callback/google&client_id=202417950362-addmtvc27k9devup7iirurrvjg6c64bi.apps.googleusercontent.com&prompt=consent`}
            >
              Reauthenticate</a>

          <button 
            className=" text-red-800 hover:underline mt-4 px-4 rounded"
            onClick={() => handleDeleteConnection()}>Delete Connection
          </button> 
          <div className='block text-red-600'>{ error }</div>

          </div>
        }
      </div>
      
     </div>
  )
}

export default GoogleConnectedApp